import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";

import type { CurrentSearchSummary } from "../../models/SearchElements";

type CurrentSearchSummaryChipsProps = {
    summary?: CurrentSearchSummary;
};

function summariseList(names: string[], max = 2): string {
    if (names.length <= max) return names.join(", ");
    return `${names.slice(0, max).join(", ")} +${names.length - max}`;
}

function formatCurrencyValue(value: number, currency?: string): string {
    const symbol =
        currency === "GBP" ? "£" :
            currency === "USD" ? "$" :
                currency === "EUR" ? "€" :
                    currency ? `${currency} ` :
                        "";

    return `${symbol}${value.toLocaleString()}`;
}

function buildSummaryChips(summary?: CurrentSearchSummary): string[] {
    if (!summary) return [];

    const chips: string[] = [];

    if (summary.portfolioName) {
        chips.push(`Portfolio: ${summary.portfolioName}`);
    }

    if (summary.searchText?.trim()) {
        chips.push(`Search: "${summary.searchText.trim()}"`);
    }

    if (summary.artistNames?.length) {
        chips.push(`Artist: ${summariseList(summary.artistNames)}`);
    }

    if (summary.categoryNames?.length) {
        chips.push(`Category: ${summariseList(summary.categoryNames)}`);
    }

    if (summary.locationNames?.length) {
        chips.push(`Location: ${summariseList(summary.locationNames)}`);
    }

    if (summary.minValue != null || summary.maxValue != null) {
        if (summary.minValue != null && summary.maxValue != null) {
            chips.push(
                `Value: ${formatCurrencyValue(summary.minValue, summary.currency)} – ${formatCurrencyValue(summary.maxValue, summary.currency)}`
            );
        } else if (summary.minValue != null) {
            chips.push(`Value ≥ ${formatCurrencyValue(summary.minValue, summary.currency)}`);
        } else if (summary.maxValue != null) {
            chips.push(`Value ≤ ${formatCurrencyValue(summary.maxValue, summary.currency)}`);
        }
    }

    if (summary.acquiredFrom || summary.acquiredTo) {
        if (summary.acquiredFrom && summary.acquiredTo) {
            chips.push(`Acquired: ${summary.acquiredFrom} – ${summary.acquiredTo}`);
        } else if (summary.acquiredFrom) {
            chips.push(`Acquired after ${summary.acquiredFrom}`);
        } else if (summary.acquiredTo) {
            chips.push(`Acquired before ${summary.acquiredTo}`);
        }
    }

    if (summary.imageStatus && summary.imageStatus != "any") {
        chips.push(`${summary.imageStatus} Image`);
    }

    if (summary.acquisitionValueStatus && summary.acquisitionValueStatus != "any") {
        chips.push(`${summary.acquisitionValueStatus} Acquisition Value`);
    }

    if (summary.insuranceValueStatus && summary.insuranceValueStatus != "any") {
        chips.push(`${summary.insuranceValueStatus} Insurance Value`);
    }

    if (summary.fairMarketValueStatus && summary.fairMarketValueStatus != "any") {
        chips.push(`${summary.fairMarketValueStatus} FMV`);
    }

    return chips;
}

export const CurrentSearchSummaryChips: React.FC<CurrentSearchSummaryChipsProps> = ({
    summary
}) => {
    const chips = buildSummaryChips(summary);

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Current Selection
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${summary?.resultCount ?? 0} artworks`}
                />

                {chips.length > 0 ? (
                    chips.map((label, index) => (
                        <Chip
                            key={`${label}-${index}`}
                            size="small"
                            variant="outlined"
                            label={label}
                        />
                    ))
                ) : (
                    <Chip
                        size="small"
                        variant="outlined"
                        label="All artworks"
                    />
                )}
            </Stack>
        </Box>
    );
};

export default CurrentSearchSummaryChips;